import React, { useEffect, useState, useRef } from 'react';

import {
    Box,
    Breadcrumbs,
    Divider,
    Link,
    Stack,
    Typography,
    Paper,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    InputAdornment,
    IconButton,
    List,
    ListSubheader,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    ListItem,
    Chip,
    Skeleton,
    FormControl,
    MenuItem,
    Select,
    InputLabel,
    Grid,
    styled
} from '@mui/material';

import { CommonIcons, icons } from '../common/CommonIcons';

import { useAuthAccess } from "../../hooks/useAuthAccess";
import { useSystemPrompt } from '../../hooks/useSystemPrompt';
import { useDocuments, documentTypes, targetTypes, configuration as documentConfiguration } from '../../hooks/api/useDocuments';
import { useTranslations } from "../../hooks/useTranslations";
import { useLoadFrom, loadFromTypes, loadFromScopes } from '../../hooks/useLoadFrom';

import { useAppContextFeatures } from '../../context/AppContext';

import { capitalizeString } from '../../utils/formatString';

const StyledIcon = styled(Box)(({ theme }) => ({
    width: 20,
    height: 20,
    // opacity: 0.1,
    //   backgroundColor: "green",
    //   color: 'inherit',
    //   display: 'flex',
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
}));

const StyledTitle = styled(Box)(({ theme }) => ({
    fontWeight: 800,
    fontSize: '1.0rem'
}))

const StyledSubTitle = styled(Box)(({ theme }) => ({
    fontWeight: 300,
    fontSize: '0.8rem'
}))

export const loadFromDocumentTypes = loadFromTypes.document

export function LoadFromDocuments(
    {
        fromDocumentId,
        fromDocumentType,
        show = false,
        filledSearchKeyword = '',
        handleOnCancel,
        handleOnSelect,
    }
) {
    const { t } = useTranslations({ ns: ["common", "prompt", "@document", "sidebar"] })

    const searchInputRef = useRef()
    const typeRef = useRef()

    const [open, setOpen] = useState(show)
    const [keyword, setKeyword] = useState(filledSearchKeyword)
    const [data, setData] = useState([])
    const [document, setDocument] = useState()

    const { getAvailableLocations } = useAuthAccess();
    const { getAvailableOptions } = useLoadFrom(fromDocumentId, fromDocumentType)

    const [documentType, setDocumentType] = useState("")
    const [documentTypeSelection, setDocumentTypeSelection] = useState([])
    const [availableOptions, setAvailableOptions] = useState([])

    const { promptMessage } = useSystemPrompt()
    const { isLoading, list, get, filterOperatorTypes } = useDocuments({});

    const { setSystemLoading } = useAppContextFeatures()

    const availableLocations = getAvailableLocations();

    const getFilterValuesLocationIds = () => {
        return availableLocations.map(i => i.id)
    }

    const getFetchDocumentsFilters = () => {

        // default filters
        let filters = [
            {
                name: "doc_number",
                operator: filterOperatorTypes.contains,
                value: keyword
            },
            {
                name: "location_id",
                operator: filterOperatorTypes.anyOf,
                value: JSON.stringify(getFilterValuesLocationIds())
            },
        ]

        // for config specify filter for documents - retail sales order
        // if (config?.filterValues) {
        //     filters = filters.concat(config?.filterValues)
        // }

        // override for only delivery orders
        if (documentType === documentTypes.deliveryOrder) {
            filters = [
                {
                    name: "doc_number",
                    operator: filterOperatorTypes.contains,
                    value: keyword,
                    group: "name_group"
                },
                {
                    name: "delivery_profile_name",
                    operator: filterOperatorTypes.eqOr,
                    value: keyword,
                    group: "name_group"
                }
            ]
        }

        // override for only cash sales
        if (
            documentType === documentTypes.cashSales
        ) {
            filters = [
                {
                    name: "doc_number",
                    operator: filterOperatorTypes.contains,
                    value: keyword,
                    group: "name_group"
                },
                {
                    name: "delivery_profile_name",
                    operator: filterOperatorTypes.eqOr,
                    value: keyword,
                    group: "name_group"
                }
            ]
        }

        // override for only replacement delivery orders
        if (
            documentType === documentTypes.replacementDelivery
        ) {
            filters = [
                {
                    name: "doc_number",
                    operator: filterOperatorTypes.contains,
                    value: keyword,
                    group: "name_group"
                },
                {
                    name: "replacement_delivery_profile_name",
                    operator: filterOperatorTypes.eqOr,
                    value: keyword,
                    group: "name_group"
                }
            ]
        }

        return filters
    }

    const getFetchDocumentsSortInfo = () => {
        return {
            id: "id",
            dir: -1,
        }
    }

    const fetchDocuments = async () => {

        try {

            let _documentType = availableOptions.filter(i => i.id === documentType)
            let docType = _documentType.length > 0 ? _documentType[0].value : ''

            if (!docType) {
                promptMessage(false, `Invalid document type`)
                return false
            }

            let filterValue = getFetchDocumentsFilters()
            let sortInfo = getFetchDocumentsSortInfo()

            let props = {
                limit: 30,
                skip: 0,
                sortInfo,
                filterValue
            }

            if (docType) props.documentType = docType

            props.configuration = {
                [documentConfiguration.enableServerClientCancellation.id]: true
            }

            let response = await list(props);
            // console.log('response?.data?.items', response?.data?.items)
            setData(response?.data?.documents ? response?.data?.documents : [])

        } catch (error) {
            promptMessage(false, `${error.message}`)
        }

        return true;

    }

    const fetchDocument = async ({ id, documentType }) => {

        let _documentType = availableOptions.filter(i => i.id === documentType)
        let docType = _documentType.length > 0 ? _documentType[0].value : ''

        if (!docType) {
            promptMessage(false, `Invalid document type`)
            return false
        }

        setSystemLoading(true);

        try {

            const props = {
                id,
                documentType: docType,
                dataTypesArray: []
            }

            // console.log('props', props)

            let response = await get(props);

            setDocument(response?.data ? response?.data : [])

            setSystemLoading(false)

            return response?.data

        } catch (error) {
            promptMessage(false, `${error.message}`)
        }

        setSystemLoading(false)

        return false;
    }

    useEffect(() => {

        setData([])

        let availableOptions = getAvailableOptions()

        if (availableOptions) {
            // console.log('availableOptions', availableOptions)

            let _documentTypeSelection = Object.keys(availableOptions).map(i => {

                let item = availableOptions[i]

                return {
                    id: `${item?.id}`,
                    value: `${item?.value}`,
                    name: `${t(item?.title)}`,
                }
            })

            setAvailableOptions(_documentTypeSelection)
            setDocumentTypeSelection(_documentTypeSelection)
            setDocumentType(fromDocumentId)

        }

    }, [])

    useEffect(() => {

        if (keyword) {
            fetchDocuments({ keyword });
        } else {
            setKeyword("")
            setData([])
        }

    }, [keyword, documentType])

    useEffect(() => {

        const timeout = setTimeout(() => {

            if (open) {
                // searchInputRef?.current.focus();

                // console.log('serachRef', searchInputRef?.current)

                if (documentTypeSelection && documentTypeSelection.length === 1) {
                    searchInputRef?.current.focus();
                } else {
                    typeRef?.current.focus();
                }
            }

        }, 100);

        return () => {
            clearTimeout(timeout);
        };

    }, [open])

    const RenderListData = () => {

        if (isLoading) {
            return (
                [1, 2, 3, 4].map((item, index) => <RenderListDataItem key={index} isLoading={Boolean(true)} />)
            )
        }

        return (
            data.map((item, index) =>
                <RenderListDataItem key={index} item={item} index={index} />
            )
        )
    }

    const RenderListDataItemSkeleton = () => {
        return (
            <>
                <Skeleton
                    animation="wave"
                    // variant="circular" 
                    sx={{
                        minWidth: 100,
                        mr: 1,
                        height: 50
                    }}
                />
                <ListItemText
                // fullWidth
                // height={50}
                >
                    <Skeleton
                        variant="rectangular"
                        width={"100%"}
                        height={20}
                    />
                    <Skeleton
                        variant="rectangular"
                        width={"100%"}
                        height={20}
                        sx={{
                            mt: 1
                        }}
                    />
                </ListItemText>
            </>
        )
    }

    const getValue = (val) => {
        if (val === "null" || val === null) {
            return "-"
        }
        return val
    }

    // item display - default
    const RenderListDataItemDisplayDefault = ({ item }) => {

        const userType = item?.user_type ? item.user_type : ''

        let {
            doc_number: docNumber,
            doc_date: docDate,
            location_name: locationName,
            profile_name: profileName,
            address_1: address1,
            address_2: address2,
            postcode,
            contact,
            email
        } = item

        docNumber = getValue(docNumber)
        docDate = getValue(docDate)
        profileName = getValue(profileName)
        locationName = getValue(locationName)
        address1 = getValue(address1)
        address2 = getValue(address2)
        postcode = getValue(postcode)
        contact = getValue(contact)
        email = getValue(email)

        return (
            <>
                <Grid container padding={1}>
                    <Grid item md={1} textAlign={"center"}>
                        <CommonIcons name={icons.documents} sx={{ width: 40, height: 40 }} />
                    </Grid>
                    <Grid item md={7}>
                        <StyledTitle sx={{ textTransform: 'uppercase' }}>
                            {`${profileName}`}
                        </StyledTitle>
                        <StyledSubTitle>
                            {`${address1}`} {`${address2}`} {`${postcode}`}
                        </StyledSubTitle>
                        <StyledSubTitle>
                            {`${contact}`} {`${email}`}
                        </StyledSubTitle>
                    </Grid>
                    <Grid item md={4} textAlign={"right"}>
                        <StyledTitle>
                            {`${docNumber}`}
                        </StyledTitle>
                        <StyledSubTitle>
                            {`${docDate}`}
                        </StyledSubTitle>
                        <StyledSubTitle>
                            {`${locationName}`}
                        </StyledSubTitle>
                        {
                            userType !== "" &&
                            <Chip
                                label={`${capitalizeString({ val: userType })}`}
                                variant={'outlined'}
                                size={"small"}
                                color={"primary"}
                            />
                        }
                    </Grid>
                </Grid>
            </>
        )

    }

    // item display - delivery order
    const RenderListDataItemDisplayDeliveryOrder = ({ item }) => {

        const userType = item?.user_type ? item.user_type : ''

        let {
            delivery_address_1: address1 = "",
            delivery_address_2: address2 = "",
            delivery_profile_name: profileName = "",
            delivery_postcode: postcode = "",
            delivery_area_name: areaName = "",
            delivery_state_name: stateName = "",
            delivery_country_name: countryName = "",
            delivery_pic_name: picName = "",
            delivery_pic_contact: picContact = "",
            delivery_pic_email: picEmail = ""
        } = item

        address1 = address1 ? address1.toUpperCase() : "-"
        address2 = address2 ? address2.toUpperCase() : "-"
        profileName = profileName ? profileName.toUpperCase() : "-"
        areaName = areaName ? areaName.toUpperCase() : "-"
        stateName = stateName ? stateName.toUpperCase() : "-"
        countryName = countryName ? countryName.toUpperCase() : "-"

        return (
            <>
                <Grid container padding={1}>
                    <Grid item md={1} textAlign={"center"}>
                        <CommonIcons name={icons.documents} sx={{ width: 40, height: 40 }} />
                    </Grid>
                    <Grid item md={7}>
                        <StyledTitle sx={{ textTransform: 'uppercase' }}>
                            {`${profileName}`}
                        </StyledTitle>
                        <StyledSubTitle>
                            {`${address1}`} {`${address2}`}
                        </StyledSubTitle>
                        <StyledSubTitle>
                            {`${areaName}`} ({`${postcode}`}) {`${stateName}`} {`${countryName}`}
                        </StyledSubTitle>
                        <StyledSubTitle>
                            {`${picName}`}, {`${picContact}`} ({`${picEmail}`})
                        </StyledSubTitle>
                    </Grid>
                    <Grid item md={4} textAlign={"right"}>
                        <StyledTitle>
                            {`${item?.doc_number}`}
                        </StyledTitle>
                        <StyledSubTitle>
                            {`${item?.doc_date}`}
                        </StyledSubTitle>
                        <StyledSubTitle>
                            {`${item?.location_name ? item.location_name : '-'}`}
                        </StyledSubTitle>
                        {
                            userType !== "" &&
                            <Chip
                                label={`${capitalizeString({ val: userType })}`}
                                variant={'outlined'}
                                size={"small"}
                                color={"primary"}
                            />
                        }
                    </Grid>
                </Grid>
            </>
        )

    }

    // item display - item transfer
    const RenderListDataItemDisplayItemTransfer = ({ item }) => {

        const userType = item?.user_type ? item.user_type : ''

        let {
            location_name: locationName = "",
            to_location_address_1: address1 = "",
            to_location_address_2: address2 = "",
            to_location_name: toLocationName = "",
            to_postcode: postcode = "",
            to_area: areaName = "",
            to_state: stateName = "",
            to_country: countryName = "",
            // delivery_pic_name: picName = "",
            // delivery_pic_contact: picContact = "",
            // delivery_pic_email: picEmail = ""
        } = item

        address1 = address1 ? address1.toUpperCase() : "-"
        address2 = address2 ? address2.toUpperCase() : "-"
        locationName = locationName ? locationName.toUpperCase() : "-"
        areaName = areaName ? areaName.toUpperCase() : "-"
        stateName = stateName ? stateName.toUpperCase() : "-"
        countryName = countryName ? countryName.toUpperCase() : "-"

        return (
            <>
                <Grid container padding={1}>
                    <Grid item md={1} textAlign={"center"}>
                        <CommonIcons name={icons.documents} sx={{ width: 40, height: 40 }} />
                    </Grid>
                    <Grid item md={7}>
                        <StyledTitle sx={{ textTransform: 'uppercase' }}>
                            {`${locationName}`}
                            <CommonIcons
                                name={icons.chevronRight}
                                sx={{ width: 15, height: 15 }}
                            />
                            {`(${toLocationName})`}
                        </StyledTitle>

                        <StyledSubTitle>
                            {`${address1}`} {`${address2}`}
                        </StyledSubTitle>
                        <StyledSubTitle>
                            {`${areaName}`} ({`${postcode}`}) {`${stateName}`} {`${countryName}`}
                        </StyledSubTitle>
                        {/* <StyledSubTitle>
                            {`${picName}`}, {`${picContact}`} ({`${picEmail}`})
                        </StyledSubTitle> */}
                    </Grid>
                    <Grid item md={4} textAlign={"right"}>
                        <StyledTitle>
                            {`${item?.doc_number}`}
                        </StyledTitle>
                        <StyledSubTitle>
                            {`${item?.doc_date}`}
                        </StyledSubTitle>
                        {
                            userType !== "" &&
                            <Chip
                                label={`${capitalizeString({ val: userType })}`}
                                variant={'outlined'}
                                size={"small"}
                                color={"primary"}
                            />
                        }
                    </Grid>
                </Grid>
            </>
        )

    }

    // item display - transfer requisition
    const RenderListDataItemDisplayTransferRequisition = ({ item }) => {

        const userType = item?.user_type ? item.user_type : ''

        let {
            location_name: locationName = "",
            to_location_address_1: address1 = "",
            to_location_address_2: address2 = "",
            to_location_name: toLocationName = "",
            to_postcode: postcode = "",
            to_area: areaName = "",
            to_state: stateName = "",
            to_country: countryName = "",
            // delivery_pic_name: picName = "",
            // delivery_pic_contact: picContact = "",
            // delivery_pic_email: picEmail = ""
        } = item

        address1 = address1 ? address1.toUpperCase() : "-"
        address2 = address2 ? address2.toUpperCase() : "-"
        locationName = locationName ? locationName.toUpperCase() : "-"
        areaName = areaName ? areaName.toUpperCase() : "-"
        stateName = stateName ? stateName.toUpperCase() : "-"
        countryName = countryName ? countryName.toUpperCase() : "-"

        return (
            <>
                <Grid container padding={1}>
                    <Grid item md={1} textAlign={"center"}>
                        <CommonIcons name={icons.documents} sx={{ width: 40, height: 40 }} />
                    </Grid>
                    <Grid item md={7}>
                        <StyledTitle sx={{ textTransform: 'uppercase' }}>
                            {`${locationName}`}
                            <CommonIcons
                                name={icons.chevronRight}
                                sx={{ width: 15, height: 15 }}
                            />
                            {`(${toLocationName})`}
                        </StyledTitle>

                        <StyledSubTitle>
                            {`${address1}`} {`${address2}`}
                        </StyledSubTitle>
                        <StyledSubTitle>
                            {`${areaName}`} ({`${postcode}`}) {`${stateName}`} {`${countryName}`}
                        </StyledSubTitle>
                        {/* <StyledSubTitle>
                            {`${picName}`}, {`${picContact}`} ({`${picEmail}`})
                        </StyledSubTitle> */}
                    </Grid>
                    <Grid item md={4} textAlign={"right"}>
                        <StyledTitle>
                            {`${item?.doc_number}`}
                        </StyledTitle>
                        <StyledSubTitle>
                            {`${item?.doc_date}`}
                        </StyledSubTitle>
                        {
                            userType !== "" &&
                            <Chip
                                label={`${capitalizeString({ val: userType })}`}
                                variant={'outlined'}
                                size={"small"}
                                color={"primary"}
                            />
                        }
                    </Grid>
                </Grid>
            </>
        )

    }

    // item display - transfer return
    const RenderListDataItemDisplayTransferReturn = ({ item }) => {

        const userType = item?.user_type ? item.user_type : ''

        let {
            location_name: locationName = "",
            to_location_address_1: address1 = "",
            to_location_address_2: address2 = "",
            to_location_name: toLocationName = "",
            to_postcode: postcode = "",
            to_area: areaName = "",
            to_state: stateName = "",
            to_country: countryName = "",
            // delivery_pic_name: picName = "",
            // delivery_pic_contact: picContact = "",
            // delivery_pic_email: picEmail = ""
        } = item

        address1 = address1 ? address1.toUpperCase() : "-"
        address2 = address2 ? address2.toUpperCase() : "-"
        locationName = locationName ? locationName.toUpperCase() : "-"
        areaName = areaName ? areaName.toUpperCase() : "-"
        stateName = stateName ? stateName.toUpperCase() : "-"
        countryName = countryName ? countryName.toUpperCase() : "-"

        return (
            <>
                <Grid container padding={1}>
                    <Grid item md={1} textAlign={"center"}>
                        <CommonIcons name={icons.documents} sx={{ width: 40, height: 40 }} />
                    </Grid>
                    <Grid item md={7}>
                        <StyledTitle sx={{ textTransform: 'uppercase' }}>
                            {`${locationName}`}
                            <CommonIcons
                                name={icons.chevronRight}
                                sx={{ width: 15, height: 15 }}
                            />
                            {`(${toLocationName})`}
                        </StyledTitle>

                        <StyledSubTitle>
                            {`${address1}`} {`${address2}`}
                        </StyledSubTitle>
                        <StyledSubTitle>
                            {`${areaName}`} ({`${postcode}`}) {`${stateName}`} {`${countryName}`}
                        </StyledSubTitle>
                        {/* <StyledSubTitle>
                            {`${picName}`}, {`${picContact}`} ({`${picEmail}`})
                        </StyledSubTitle> */}
                    </Grid>
                    <Grid item md={4} textAlign={"right"}>
                        <StyledTitle>
                            {`${item?.doc_number}`}
                        </StyledTitle>
                        <StyledSubTitle>
                            {`${item?.doc_date}`}
                        </StyledSubTitle>
                        {
                            userType !== "" &&
                            <Chip
                                label={`${capitalizeString({ val: userType })}`}
                                variant={'outlined'}
                                size={"small"}
                                color={"primary"}
                            />
                        }
                    </Grid>
                </Grid>
            </>
        )

    }

    // trip order
    const RenderListDataItemDisplayTripOrder = ({ item }) => {

        const userType = item?.user_type ? item.user_type : ''

        return (
            <>
                <Grid container padding={1}>
                    <Grid item md={1} textAlign={"center"}>
                        <CommonIcons name={icons.documents} sx={{ width: 40, height: 40 }} />
                    </Grid>
                    <Grid item md={7}>
                        <StyledTitle sx={{ textTransform: 'uppercase' }}>
                            {`${item?.location_name}`}
                        </StyledTitle>
                        <StyledSubTitle>
                            {`${item?.driver_name}`} {`(${item?.logistic_label})`}
                        </StyledSubTitle>
                        <StyledSubTitle>
                            {`Related Docs: ${item?.docs.length}`}
                        </StyledSubTitle>
                    </Grid>
                    <Grid item md={4} textAlign={"right"}>
                        <StyledTitle>
                            {`${item?.doc_number}`}
                        </StyledTitle>
                        <StyledSubTitle>
                            {`${item?.doc_date}`}
                        </StyledSubTitle>
                        <StyledSubTitle>
                            {`${item?.location_name ? item.location_name : '-'}`}
                        </StyledSubTitle>
                        {
                            userType !== "" &&
                            <Chip
                                label={`${capitalizeString({ val: userType })}`}
                                variant={'outlined'}
                                size={"small"}
                                color={"primary"}
                            />
                        }
                    </Grid>
                </Grid>
            </>
        )

    }

    const RenderListDataItemDisplay = (props) => {

        const { item } = props

        if (item?.type.toLowerCase() === documentTypes.deliveryOrder.toLowerCase()) {
            return <RenderListDataItemDisplayDeliveryOrder {...props} />
        }

        if (item?.type.toLowerCase() === documentTypes.itemTransfer.toLowerCase()) {
            return <RenderListDataItemDisplayItemTransfer {...props} />
        }

        if (item?.type.toLowerCase() === documentTypes.transferRequisition.toLowerCase()) {
            return <RenderListDataItemDisplayTransferRequisition {...props} />
        }

        if (item?.type.toLowerCase() === documentTypes.transferReturn.toLowerCase()) {
            return <RenderListDataItemDisplayTransferReturn {...props} />
        }

        if (item?.type.toLowerCase() === documentTypes.tripOrder.toLowerCase()) {
            return <RenderListDataItemDisplayTripOrder {...props} />
        }

        return (
            <RenderListDataItemDisplayDefault {...props} />
        )
    }

    const RenderListDataItem = ({ isLoading = false, index, item }) => {
        return (
            <ListItem
                key={index}
                divider
                disablePadding
            >
                <ListItemButton
                    role={undefined}
                    onClick={() => {
                        handleSelect({ item, id: item?.id });
                    }}
                    dense
                >
                    {
                        Boolean(isLoading) ? <RenderListDataItemSkeleton /> : <RenderListDataItemDisplay item={item} />

                    }

                </ListItemButton>
            </ListItem>
        )
    }

    const RenderList = () => {

        return (
            <List
                // sx={{ width: '100%', maxWidth: 360,
                //     bgcolor: 'background.paper' }}
                component="nav"
                aria-labelledby="nested-list-subheader"
                subheader={
                    <ListSubheader
                        component="div"
                        id="nested-list-subheader"
                        sx={{
                            // backgroundColor: 'red'
                            // pl: 1,
                            // pr: 1
                        }}
                    >
                        <Paper
                            elevation={3}
                            sx={{
                                p: 1,
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}
                        >

                            <Box
                                sx={{
                                    flex: 1,
                                    mr: 1
                                }}
                            >

                                <FormControl
                                    margin="dense"
                                    fullWidth
                                    size="small"
                                // error={touched.countryId && Boolean(errors.countryId)}
                                >
                                    <InputLabel id="documentType">{t('document')}</InputLabel>
                                    <Select
                                        inputRef={typeRef}
                                        labelId="documentType"
                                        label={t('document')}
                                        name={"documentType"}
                                        onChange={(event) => {
                                            setDocumentType(event.target.value)
                                        }}
                                        value={documentType}
                                        sx={{
                                            textAlign: 'left'
                                        }}
                                    >
                                        {
                                            documentTypeSelection.map((i, index) => {
                                                return (
                                                    <MenuItem key={index} value={i.id}>{i.name}</MenuItem>
                                                )
                                            })
                                        }
                                    </Select>
                                    {/* <FormHelperText>
                                            {touched.countryId && errors.countryId}
                                        </FormHelperText> */}
                                </FormControl>
                            </Box>
                            <Box
                                sx={{
                                    flex: 2,
                                }}
                            >
                                <TextField
                                    inputRef={searchInputRef}
                                    autoComplete="off"
                                    margin="dense"
                                    size="small"
                                    label={t("searchKeyword")}
                                    fullWidth
                                    variant="outlined"
                                    onChange={e => {
                                        setKeyword(e.target.value)
                                    }}
                                    value={keyword}
                                    InputProps={{
                                        startAdornment:
                                            <InputAdornment position="start">
                                                <CommonIcons name={icons.search} />
                                            </InputAdornment>
                                    }}
                                />
                            </Box>
                        </Paper>
                    </ListSubheader>
                }
            >
                {
                    RenderListData()
                }

            </List>
        )
    }

    const handleExit = () => {
        setOpen(false);

        if (handleOnCancel)
            handleOnCancel();
    }

    const handleSelect = async ({ id, item }) => {

        let document = await fetchDocument({
            id,
            documentType
        })

        if (handleOnSelect)
            handleOnSelect({ id, data: document })

        promptMessage(true, t(`selected1Item`))
        setOpen(false);
    }

    return (
        <Box>

            {/* { RenderSearch() } */}

            <Dialog
                sx={{
                    '& .MuiDialog-paper':
                    {
                        // width: '80%', 
                        minHeight: '90vh'
                    }
                }}
                fullWidth={Boolean(true)}
                maxWidth="md"
                // TransitionProps={{ onEntering: handleEntering }}
                open={open}
                // {...other}
                onClose={handleExit}
            >
                <DialogTitle
                >
                    {t('loadFromDocuments')}

                    <IconButton
                        aria-label="close"
                        onClick={handleExit}
                        sx={{
                            position: 'absolute',
                            right: 10,
                            top: 10,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CommonIcons name={icons.cancel} />
                    </IconButton>

                </DialogTitle>
                <DialogContent
                    dividers
                    // minHeight="100%"
                    sx={{
                        // backgroundColor: 'red',
                        pl: 0,
                        pr: 0
                    }}
                >
                    {/* {JSON.stringify(availableLocations)} */}
                    {
                        RenderList()
                    }

                </DialogContent>
            </Dialog>

        </Box>
    )
}